
import styles from '../style.module.css';
import { motion, useReducedMotion } from "framer-motion";
import { useEffect, useState } from "react";

function parseStatValue(value) {
    const raw = String(value ?? "");
    const match = raw.match(/^(\D*)(\d+(?:[.,]\d+)?)(.*)$/);
    if (!match) {
        return { prefix: "", number: null, suffix: raw };
    }
    return {
        prefix: match[1] || "",
        number: Number(String(match[2]).replace(",", ".")),
        suffix: match[3] || "",
    };
}

function easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
}

export default function BannerRotateItem({
    value,
    text,
    background,
    valueColor,
    textColor,
    rotate = 0,
    left = 0,
    delay = 0,
    index = 0,
}) {
    const reduceMotion = useReducedMotion();
    const { prefix, number, suffix } = parseStatValue(value);
    const [displayValue, setDisplayValue] = useState(
        number == null ? String(value ?? "") : `${prefix}0${suffix}`
    );

    useEffect(() => {
        if (number == null) {
            setDisplayValue(String(value ?? ""));
            return undefined;
        }

        if (reduceMotion) {
            setDisplayValue(`${prefix}${Math.round(number)}${suffix}`);
            return undefined;
        }

        let rafId = 0;
        const duration = 1100;
        const startAt = performance.now() + delay * 1000 + 180;

        const tick = (now) => {
            if (now < startAt) {
                rafId = requestAnimationFrame(tick);
                return;
            }
            const progress = Math.min(1, (now - startAt) / duration);
            const current = Math.round(easeOutCubic(progress) * number);
            setDisplayValue(`${prefix}${current}${suffix}`);
            if (progress < 1) {
                rafId = requestAnimationFrame(tick);
            }
        };

        rafId = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(rafId);
    }, [delay, number, prefix, reduceMotion, suffix, value]);

    return (
        <motion.div
            className={styles.banner_rotate_item_wrap}
            style={{ marginLeft: `${left}px` }}
            initial={
                reduceMotion
                    ? false
                    : { opacity: 0, y: 42, scale: 0.88 }
            }
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{
                delay,
                duration: 0.7,
                ease: [0.22, 1, 0.36, 1],
            }}
            whileHover={
                reduceMotion
                    ? undefined
                    : {
                        y: -6,
                        scale: 1.03,
                        transition: { type: "spring", stiffness: 260, damping: 18 },
                    }
            }
        >
            <div
                className={`${styles.banner_rotate_item} ${reduceMotion ? "" : styles.banner_rotate_item_float}`}
                style={{
                    background,
                    ["--card-rotate"]: `${rotate}deg`,
                    animationDelay: `${index * 0.4}s`,
                }}
            >
                <h2 style={{ color: valueColor }}>
                    {displayValue}
                </h2>
                <span style={{ color: textColor }}>
                    {text}
                </span>
            </div>
        </motion.div>
    );
}
